import { ScheduleIcon } from '@/components/icons/schedule-icon'
import { Button, ButtonProps } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { toast } from '@/components/ui/toaster'
import { tooltipIconButtonVariants } from '@/components/ui/tooltip-icon-button'
import { useDeleteAgentMutation } from '@/hooks/api/use-delete-agent-mutation'
import { cn } from '@/lib/utils'
import { DropdownMenuContentProps } from '@radix-ui/react-dropdown-menu'
import { T, useTranslate } from '@tolgee/react'
import { EllipsisVertical, Trash2Icon } from 'lucide-react'
import React from 'react'
import { ScheduleAddDialog } from '../schedule/_components/add-dialog'
import { DeleteAgentDialog } from './delete-agent-dialog'

type AgentsOptionsMenuProps = Readonly<
  {
    agentId: string
    agentName: string
    agentType: string
    className?: string
  } & Pick<DropdownMenuContentProps, 'align' | 'alignOffset'>
>

function AgentsOptionsMenu({
  agentId,
  agentName,
  agentType,
  className,
  align = 'center',
  alignOffset,
}: AgentsOptionsMenuProps) {
  const { t } = useTranslate()
  const [open, setOpen] = React.useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false)
  const [scheduleDialogOpen, setScheduleDialogOpen] = React.useState(false)

  const deleteAgentMutation = useDeleteAgentMutation(agentId)

  const handleDelete = () => {
    setDeleteDialogOpen(true)
    setOpen(false)
  }

  const handleSchedule = () => {
    setScheduleDialogOpen(true)
    setOpen(false)
  }

  const confirmDelete = () => {
    deleteAgentMutation.mutate(
      { agentId },
      {
        onSuccess: () => {
          setDeleteDialogOpen(false)
          toast.success(t('agentRegistry.deleteDialog.success', { agentName }))
        },
        onError: () => {
          toast.error(t('agentRegistry.deleteDialog.error'))
        },
      },
    )
  }

  return (
    <>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger
          className={cn(tooltipIconButtonVariants(), className)}
          title={t('agentRegistry.optionsMenu.title')}
        >
          <EllipsisVertical className='text-neutral-150' />
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align={align}
          alignOffset={alignOffset}
          className='w-40 border border-white bg-neutral-25 p-1.5 shadow-sidebar'
        >
          <DropdownMenuItem asChild unstyled>
            <AgentsOptionButton onClick={handleSchedule} disabled={agentType !== 'workflow'}>
              <ScheduleIcon className='size-6 shrink-0 text-neutral-150' />
              <T keyName='agentRegistry.optionsMenu.schedule' />
            </AgentsOptionButton>
          </DropdownMenuItem>
          <DropdownMenuItem asChild unstyled>
            <AgentsOptionButton
              onClick={handleDelete}
              disabled={!(agentType === 'generated' || agentType === 'workflow')}
            >
              <Trash2Icon className='mx-1 size-4 shrink-0 text-error-300' />
              <p className='text-error-300'>
                <T keyName='agentRegistry.optionsMenu.delete' />
              </p>
            </AgentsOptionButton>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <ScheduleAddDialog
        open={scheduleDialogOpen}
        onOpenChange={setScheduleDialogOpen}
        agentId={agentId}
      />

      <DeleteAgentDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        agentName={agentName}
        onConfirm={confirmDelete}
        isLoading={deleteAgentMutation.isPending}
      />
    </>
  )
}

const AgentsOptionButton = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ children, className, ...props }, ref) => {
    return (
      <Button
        ref={ref}
        unstyled
        className={cn(
          'flex h-10 w-full select-none items-center gap-2 whitespace-nowrap rounded px-2 text-sm font-light tracking-wide outline-none hover:bg-neutral-75 focus:bg-neutral-75 disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
        {...props}
      >
        {children}
      </Button>
    )
  },
)
AgentsOptionButton.displayName = 'AgentsOptionButton'

export { AgentsOptionsMenu, type AgentsOptionsMenuProps }
